/**
 * ==============================================================================
 * TECHNIQUE DETAIL MODAL COMPONENT
 * ==============================================================================
 * Modal component to display detailed information about a specific MITRE technique
 * Shows mapped controls, coverage statistics, and allows for control management
 * Version: 2.0 | Date: 2025-07-27
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useApi } from '../contexts/ApiContext';
import { Shield, ExternalLink, AlertCircle, CheckCircle, XCircle, BarChart, Target, Clock, Info } from 'lucide-react';

/**
 * TechniqueModal Component
 * Full implementation with data fetching and control management
 * 
 * @param {string} techniqueId - The ID of the technique to display
 * @param {Function} onClose - Callback to close the modal
 */
const TechniqueModal = ({ techniqueId, onClose }) => {
    // State management
    const [isClosing, setIsClosing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [techniqueData, setTechniqueData] = useState(null);
    const [mappingStatuses, setMappingStatuses] = useState({});
    const [updatingMappings, setUpdatingMappings] = useState({});

    // Get API client from hook
    const { apiClient } = useApi();

    /**
     * Fetch technique details from API
     */
    const fetchTechniqueDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get(`/api/dashboard/techniques/${techniqueId}`);
            
            if (response.data && response.data.success && response.data.technique) {
                setTechniqueData(response.data);
                
                // Initialize mapping statuses
                const statuses = {};
                response.data.technique.mappedControls.forEach(mapping => {
                    statuses[mapping.mappingId] = mapping.status;
                });
                setMappingStatuses(statuses);
            } else {
                throw new Error(response.data?.message || 'Failed to load technique details');
            }
        } catch (err) {
            console.error('Error fetching technique details:', err);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle mapping status update (approve/reject)
     */
    const handleMappingUpdate = async (mappingId, newStatus) => {
        try {
            setUpdatingMappings(prev => ({ ...prev, [mappingId]: true }));

            // Note: This endpoint would need to be implemented in the backend
            const response = await apiClient.patch(`/api/mappings/${mappingId}`, {
                status: newStatus
            });

            if (response.data && response.data.success) {
                // Update local state
                setMappingStatuses(prev => ({ ...prev, [mappingId]: newStatus }));
                
                // Update the technique data
                setTechniqueData(prev => ({
                    ...prev,
                    technique: {
                        ...prev.technique,
                        mappedControls: prev.technique.mappedControls.map(mapping =>
                            mapping.mappingId === mappingId
                                ? { ...mapping, status: newStatus }
                                : mapping
                        )
                    }
                }));
            }
        } catch (err) {
            console.error('Error updating mapping:', err);
            // Could show a toast notification here
        } finally {
            setUpdatingMappings(prev => ({ ...prev, [mappingId]: false }));
        }
    };

    /**
     * Load data when component mounts or techniqueId changes
     */
    useEffect(() => {
        if (techniqueId) {
            fetchTechniqueDetails();
        }
    }, [techniqueId]);

    /**
     * Handle escape key press to close modal
     */
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    /**
     * Handle modal close with animation
     */
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 200); // Match animation duration
    };

    /**
     * Prevent click propagation from modal content
     */
    const handleContentClick = (e) => {
        e.stopPropagation();
    };

    /**
     * Get coverage level style and label
     */
    const getCoverageInfo = (technique) => {
        if (!technique || !technique.mappedControls) return { label: 'No Coverage', color: 'red' };
        
        const passControls = technique.mappedControls.filter(m => m.control.status === 'Pass').length;
        const totalControls = technique.mappedControls.length;
        
        if (totalControls === 0) return { label: 'No Coverage', color: 'red' };
        if (passControls >= 2) return { label: 'Excellent', color: 'green' };
        if (passControls >= 1) return { label: 'Good', color: 'green' };
        return { label: 'Partial', color: 'yellow' };
    };

    /**
     * Get risk level style and label
     */
    const getRiskInfo = (technique) => {
        const coverage = getCoverageInfo(technique);
        if (coverage.label === 'No Coverage') return { label: 'Critical', color: 'red' };
        if (coverage.label === 'Partial') return { label: 'High', color: 'orange' };
        if (coverage.label === 'Good') return { label: 'Medium', color: 'yellow' };
        return { label: 'Low', color: 'green' };
    };

    /**
     * Render content based on state
     */
    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mb-4">
                        <svg className="animate-spin h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    <p className="text-gray-500">Loading technique details...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Technique</h3>
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            );
        }

        if (!techniqueData || !techniqueData.technique) {
            return null;
        }

        const { technique, stats } = techniqueData;
        const coverageInfo = getCoverageInfo(technique);
        const riskInfo = getRiskInfo(technique);

        return (
            <>
                {/* Technique Information Card */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-200">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center mb-2">
                                <span className="text-indigo-600 font-mono font-bold text-lg mr-2">{technique.id}</span>
                                <span className="text-gray-400">•</span>
                                <h3 className="text-xl font-bold text-gray-900 ml-2">
                                    {technique.name}
                                </h3>
                            </div>
                            <p className="text-gray-700 leading-relaxed">{technique.description}</p>
                        </div>
                        {technique.url && (
                            <a
                                href={technique.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-4 inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors"
                            >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                MITRE Reference
                            </a>
                        )}
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                            <Shield className="h-5 w-5 text-gray-400" />
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                coverageInfo.label === 'Excellent' ? 'bg-green-100 text-green-800' :
                                coverageInfo.label === 'Good' ? 'bg-green-100 text-green-800' :
                                coverageInfo.label === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                            }`}>
                                {coverageInfo.label}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Coverage Level</div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                            <AlertCircle className="h-5 w-5 text-gray-400" />
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                riskInfo.label === 'Low' ? 'bg-green-100 text-green-800' :
                                riskInfo.label === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                riskInfo.label === 'High' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                            }`}>
                                {riskInfo.label}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Risk Level</div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                            <BarChart className="h-5 w-5 text-gray-400" />
                            <span className="text-lg font-bold text-blue-600">
                                {stats.avgConfidence ? `${Math.round(stats.avgConfidence * 100)}%` : 'N/A'}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Avg. Confidence</div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                            <Info className="h-5 w-5 text-gray-400" />
                            <span className="text-lg font-bold text-gray-900">{stats.totalControls}</span>
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Mapped Controls</div>
                    </div>
                </div>

                {/* Mapped Controls Section */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                                Mapped Security Controls
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">Controls currently protecting against this technique</p>
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                            {technique.mappedControls.length} controls mapped
                        </span>
                    </div>
                    
                    {technique.mappedControls.length === 0 ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                            <p className="text-red-800 font-medium">No security controls are currently mapped to this technique.</p>
                            <p className="text-sm text-red-600 mt-1">This represents a critical security gap that should be addressed.</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                            {technique.mappedControls.map((mapping) => (
                                <div key={mapping.mappingId} className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            {/* Control Header */}
                                            <div className="flex items-start space-x-3 mb-3">
                                                <div className="flex-shrink-0">
                                                    <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-mono font-semibold bg-gray-900 text-white">
                                                        {mapping.control.id}
                                                    </span>
                                                </div>
                                                <div className="flex-1">
                                                    <h5 className="font-semibold text-gray-900">{mapping.control.title}</h5>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            mapping.control.status === 'Pass' 
                                                                ? 'bg-green-100 text-green-800' 
                                                                : 'bg-red-100 text-red-800'
                                                        }`}>
                                                            {mapping.control.status === 'Pass' ? '✓' : '✗'} {mapping.control.status}
                                                        </span>
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            ☁️ {mapping.control.cloudProvider}
                                                        </span>
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                            {mapping.control.framework}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Control Description */}
                                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                                <p className="text-sm text-gray-700 leading-relaxed">{mapping.control.description}</p>
                                            </div>
                                            
                                            {/* Mapping Metadata */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center">
                                                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{width: '80px'}}>
                                                            <div 
                                                                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                                                style={{width: `${mapping.confidence * 80}px`}}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-medium text-gray-600">
                                                            {Math.round(mapping.confidence * 100)}% confidence
                                                        </span>
                                                    </div>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        mappingStatuses[mapping.mappingId] === 'approved' ? 'bg-green-100 text-green-800' : 
                                                        mappingStatuses[mapping.mappingId] === 'rejected' ? 'bg-red-100 text-red-800' : 
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {mappingStatuses[mapping.mappingId] === 'approved' ? '✓ Approved' :
                                                         mappingStatuses[mapping.mappingId] === 'rejected' ? '✗ Rejected' :
                                                         '⏳ ' + (mappingStatuses[mapping.mappingId] || mapping.status)}
                                                    </span>
                                                </div>
                                                {mapping.rationale && (
                                                    <span className="text-xs text-gray-500 italic">
                                                        "{mapping.rationale}"
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="ml-4 flex flex-col space-y-2">
                                            <button
                                                onClick={() => handleMappingUpdate(mapping.mappingId, 'approved')}
                                                disabled={mappingStatuses[mapping.mappingId] === 'approved' || updatingMappings[mapping.mappingId]}
                                                className={`inline-flex items-center justify-center p-2 rounded-lg transition-all ${
                                                    mappingStatuses[mapping.mappingId] === 'approved'
                                                        ? 'bg-green-100 text-green-700 cursor-default'
                                                        : 'bg-white text-green-600 hover:bg-green-50 border border-green-300 hover:shadow-sm'
                                                } disabled:opacity-50`}
                                                title="Approve mapping"
                                            >
                                                {updatingMappings[mapping.mappingId] ? (
                                                    <Clock className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <CheckCircle className="w-5 h-5" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleMappingUpdate(mapping.mappingId, 'rejected')}
                                                disabled={mappingStatuses[mapping.mappingId] === 'rejected' || updatingMappings[mapping.mappingId]}
                                                className={`inline-flex items-center justify-center p-2 rounded-lg transition-all ${
                                                    mappingStatuses[mapping.mappingId] === 'rejected'
                                                        ? 'bg-red-100 text-red-700 cursor-default'
                                                        : 'bg-white text-red-600 hover:bg-red-50 border border-red-300 hover:shadow-sm'
                                                } disabled:opacity-50`}
                                                title="Reject mapping"
                                            >
                                                {updatingMappings[mapping.mappingId] ? (
                                                    <Clock className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <XCircle className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div 
            className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-200 ${
                isClosing ? 'opacity-0' : 'opacity-100'
            }`}
            onClick={handleClose}
        >
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

            {/* Modal Content */}
            <div className="flex min-h-screen items-center justify-center p-4">
                <div 
                    className={`relative bg-white rounded-lg shadow-xl max-w-4xl w-full transform transition-all duration-200 ${
                        isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
                    }`}
                    onClick={handleContentClick}
                >
                    {/* Modal Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                    <Target className="h-8 w-8 text-indigo-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">MITRE ATT&CK Technique Details</h2>
                                    <p className="text-sm text-gray-500 mt-1">Security coverage analysis and control mappings</p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100"
                            >
                                <XCircle className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Modal Body */}
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                        {renderContent()}
                    </div>

                    {/* Modal Footer */}
                    <div className="sticky bottom-0 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg">
                        <div className="flex justify-between items-center">
                            <div className="text-sm text-gray-500">
                                {techniqueData && techniqueData.lastUpdated && (
                                    <span className="flex items-center">
                                        <Clock className="h-4 w-4 mr-1" />
                                        Last updated: {new Date(techniqueData.lastUpdated).toLocaleString()}
                                    </span>
                                )}
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors inline-flex items-center"
                                >
                                    <Shield className="h-4 w-4 mr-2" />
                                    Refresh Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// PropTypes validation
TechniqueModal.propTypes = {
    techniqueId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired
};

export default TechniqueModal;